import { createSlice } from '@reduxjs/toolkit'
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils'
import fetchBizCategories from 'features/channel/add/thunk/fetchBizCategories'
import fetchTalkChannels from 'features/channel/add/thunk/fetchTalkChannels'
import checkUnifiedUrl from 'features/channel/add/thunk/checkUnifiedUrl'
import checkMobileUrl from 'features/channel/add/thunk/checkMobileUrl'
import checkPcUrl from 'features/channel/add/thunk/checkPcUrl'
import uploadDocument from 'features/channel/add/thunk/uploadDocument'
import fetchEditingChannel from 'features/channel/add/thunk/fetchEditingChannel'
import addChannel from 'features/channel/add/thunk/addChannel'
import requestReview from 'features/channel/add/thunk/requestReview'
import { BizChannelTypes } from '../../Codes'

export const initialState = {
  bizChannelType: BizChannelTypes.WEBSITE_BIZ_CHANNEL,
  bizChannelId: null,
  apiError: null,
  isLoading: false,
  name: null,
  isUrlIndividual: false,
  unifiedUrl: null,
  mobileUrl: null,
  pcUrl: null,
  isUnifiedUrlChecked: null,
  isMobileUrlChecked: null,
  isPcUrlChecked: null,
  hasAccessAccount: false,
  accessAccountId: null,
  accessAccountPassword: null,
  kakaoTalkChannels: [],
  reviewDocuments: [],
  bizCategories: [],
  isCategoryLoading: true,
  selectedCategory: {
    name: '',
    children: [],
  },
  selectedCategoryDetail: {},
  oldCategoryFullPath: null,
  oldKakaoTalkChannels: null,
  oldReviewDocuments: null,
  reviewStatus: null,
}

const channelAddSlice = createSlice({
  name: 'channelAdd',
  initialState,
  reducers: {
    reset: () => initialState,
    setIsLimitedSite: (state, { payload }) => {
      state.hasAccessAccount = payload
    },
    setLimitedSiteId: (state, { payload }) => {
      state.accessAccountId = payload
    },
    setLimitedSitePassword: (state, { payload }) => {
      state.accessAccountPassword = payload
    },
    setSiteName: (state, { payload }) => {
      state.name = payload
    },
    setUrlIndividual: (state, { payload: isUrlIndividual }) => {
      state.isUrlIndividual = isUrlIndividual
    },
    setUnifiedUrl: (state, { payload }) => {
      state.unifiedUrl = payload
      state.isUnifiedUrlChecked = null
    },
    setMobileUrl: (state, { payload }) => {
      state.mobileUrl = payload
      state.isMobileUrlChecked = null
    },
    setPcUrl: (state, { payload }) => {
      state.pcUrl = payload
      state.isPcUrlChecked = null
    },
    setSelectedCategory: (state, { payload }) => {
      state.selectedCategory = payload
      state.selectedCategoryDetail = null
      state.reviewDocuments = []
    },
    setSelectedCategoryDetail: (state, { payload }) => {
      state.selectedCategoryDetail = payload
      state.reviewDocuments = (
        (payload && payload.reviewRequiredDocuments) ||
        []
      ).map(({ id, name, reviewDocumentType }) => ({
        id,
        name,
        reviewDocumentType,
        files: [],
      }))
    },
    toggleTalkChannel: (state, { payload: uuid }) => {
      const index = state.kakaoTalkChannels.findIndex(
        channel => channel.uuid === uuid
      )
      if (index !== -1) {
        const { isSelected } = state.kakaoTalkChannels[index]
        state.kakaoTalkChannels[index].isSelected = !isSelected
      }
    },
    setCategoryByFullPath: state => {
      if (isNullOrUndefined(state.oldCategoryFullPath)) return
      const [cateName, detailCateName] = state.oldCategoryFullPath
        .split('>')
        .map(name => name.trim())

      const selectedCategory = state.bizCategories.find(
        ({ name }) => name === cateName
      )
      if (selectedCategory) state.selectedCategory = selectedCategory

      const selectedCategoryDetail = state.selectedCategory.children.find(
        ({ name }) => name === detailCateName
      )
      if (selectedCategoryDetail) {
        state.selectedCategoryDetail = selectedCategoryDetail
        state.reviewDocuments = (
          selectedCategoryDetail.reviewRequiredDocuments || []
        ).map(({ id, name }) => ({
          id,
          name,
          files: [],
        }))
      }
    },
    setKakaoTalkChannelsFromOldData: state => {
      if (isNullOrUndefinedOrEmpty(state.oldKakaoTalkChannels)) return
      state.oldKakaoTalkChannels.forEach(({ uuid }) => {
        Object.assign(
          state.kakaoTalkChannels.find(
            talkChannel => talkChannel.uuid === uuid
          ) || {},
          {
            isSelected: true,
          }
        )
      })
    },
    setReviewDocumentsFromOldData: state => {
      state.oldReviewDocuments.forEach(
        ({ reviewRequiredDocumentId, url, filename }) => {
          const reviewDocument = state.reviewDocuments.find(
            document => +document.id === +reviewRequiredDocumentId
          )
          if (reviewDocument) {
            reviewDocument.files.push({
              filename,
              path: new URL(url).pathname,
            })
          }
        }
      )
    },
    setBizChannelId: (state, { payload: bizChannelId }) => {
      state.bizChannelId = bizChannelId
    },
    deleteDocumentFile: (state, { payload: { id, path } }) => {
      const reviewDocument = state.reviewDocuments.find(
        document => document.id === id
      )
      reviewDocument.files = reviewDocument.files.filter(
        file => file.path !== path
      )
    },
    setBizChannelType: (state, { payload: bizChannelType }) => {
      state.bizChannelType = bizChannelType
      state.name = null
      state.isUrlIndividual = bizChannelType === BizChannelTypes.TALK_CHANNEL
      state.unifiedUrl = null
      state.mobileUrl = null
      state.pcUrl = null
      state.isUnifiedUrlChecked = null
      state.isMobileUrlChecked = null
      state.isPcUrlChecked = null
      state.hasAccessAccount = false
      state.accessAccountId = null
      state.accessAccountPassword = null
      state.selectedCategory = {
        name: '',
        children: [],
      }
      state.selectedCategoryDetail = {}
      state.reviewDocuments = []
      state.kakaoTalkChannels = state.kakaoTalkChannels.map(
        ({ isSelected, ...rest }) => ({
          ...rest,
          isSelected: false,
        })
      )
    },
  },
  extraReducers: builder => {
    fetchEditingChannel.reducers(builder)
    fetchBizCategories.reducers(builder)
    fetchTalkChannels.reducers(builder)
    checkUnifiedUrl.reducers(builder)
    checkMobileUrl.reducers(builder)
    checkPcUrl.reducers(builder)
    addChannel.reducers(builder)
    uploadDocument.reducers(builder)
    requestReview.reducers(builder)
  },
})

export const {
  reset,
  setSiteName,
  setSelectedCategory,
  setSelectedCategoryDetail,
  setIsLimitedSite,
  setLimitedSiteId,
  setLimitedSitePassword,
  setUrlIndividual,
  setMobileUrl,
  setPcUrl,
  setUnifiedUrl,
  toggleTalkChannel,
  setCategoryByFullPath,
  setKakaoTalkChannelsFromOldData,
  setReviewDocumentsFromOldData,
  setBizChannelId,
  deleteDocumentFile,
  setBizChannelType,
} = channelAddSlice.actions

export default channelAddSlice.reducer
